/**
 * Streak Widget Component
 * Displays user's current streak with progress and milestones
 */

import React, { useState, useEffect } from 'react';
import { gamificationService } from '../services/gamificationService';
import { Flame, Snowflake, Trophy } from 'lucide-react';

export default function StreakWidget({ compact = false }) {
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStreak();
  }, []);

  const loadStreak = async () => {
    try {
      const data = await gamificationService.getStreak();
      setStreak(data);
    } catch (error) {
      console.error('Failed to load streak:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNextMilestone = (current) => {
    if (current < 7) return 7;
    if (current < 30) return 30;
    if (current < 100) return 100;
    return Math.ceil(current / 100) * 100 + 100;
  };

  const getMilestoneProgress = (current) => {
    const next = getNextMilestone(current);
    if (next === 7) return (current / 7) * 100;
    if (next === 30) return ((current - 7) / 23) * 100;
    if (next === 100) return ((current - 30) / 70) * 100;
    const base = Math.floor(current / 100) * 100;
    return ((current - base) / 100) * 100;
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-gray-100 rounded-lg p-4 h-24" />
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full px-3 py-1.5">
        <Flame className="w-4 h-4" />
        <span className="font-bold text-sm">{streak?.current_streak || 0}</span>
        {streak?.streak_freeze_available && (
          <Snowflake className="w-3 h-3 ml-1 opacity-75" />
        )}
      </div>
    );
  }

  const nextMilestone = getNextMilestone(streak?.current_streak || 0);
  const progress = getMilestoneProgress(streak?.current_streak || 0);

  return (
    <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-orange-500 to-red-500 p-3 rounded-full">
            <Flame className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-3xl font-bold text-gray-900">
              {streak?.current_streak || 0}
              <span className="text-lg ml-2 text-gray-600">day streak</span>
            </div>
            <div className="text-sm text-gray-600 flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              Best: {streak?.longest_streak || 0} days
            </div>
          </div>
        </div>
        
        {streak?.streak_freeze_available && (
          <div className="bg-blue-100 text-blue-700 px-3 py-2 rounded-lg flex items-center gap-2">
            <Snowflake className="w-5 h-5" />
            <span className="text-sm font-medium">Freeze Available</span>
          </div>
        )}
      </div>

      {/* Progress to next milestone */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-gray-600">
          <span>Next milestone</span>
          <span className="font-medium">{nextMilestone} days</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Streak breakdown */}
      {streak && (
        <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-orange-200">
          <div className="text-center">
            <div className="text-lg font-bold text-orange-600">{streak.drill_streak || 0}</div>
            <div className="text-xs text-gray-600">Drills</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-orange-600">{streak.call_streak || 0}</div>
            <div className="text-xs text-gray-600">Calls</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-orange-600">{streak.objection_streak || 0}</div>
            <div className="text-xs text-gray-600">Objections</div>
          </div>
        </div>
      )}
    </div>
  );
}
